import { Bookmark, BookmarkPlus, Save, Trash2, Users } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import type { SavedViewRow, SavedViewState } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Checkbox } from '../../../components/Checkbox';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { MenuGroup, MenuItem, MenuSeparator, Popover, usePopover } from '../../../components/Popover';
import { errorMessage } from '../../../lib/api-client';
import { useToasts } from '../../../stores/toasts';
import { useDeleteView, useSavedViews, useSaveView, useUpdateView } from '../api/pipeline-api';
import styles from './PipelineToolbar.module.css';

interface SavedViewsMenuProps {
  /** The view in use, from the address. */
  readonly currentId: string | undefined;
  /** What the screen shows now — what "Save" stores. */
  readonly current: SavedViewState;
  readonly onApply: (view: SavedViewRow) => void;
}

/**
 * F8.14 — named filter, column and layout combinations, private or shared with the
 * team (06-qol §3). Choosing one is the same as opening a colleague's link to it.
 */
export function SavedViewsMenu({ currentId, current, onApply }: SavedViewsMenuProps): ReactNode {
  const views = useSavedViews();
  const save = useSaveView();
  const update = useUpdateView();
  const remove = useDeleteView();
  const show = useToasts((state) => state.show);
  const { isOpen, anchor, triggerRef, toggle, close } = usePopover();
  const [isNaming, setIsNaming] = useState(false);
  const [name, setName] = useState('');
  const [isShared, setIsShared] = useState(false);

  const all = views.data ?? [];
  const mine = all.filter((view) => view.isMine);
  const shared = all.filter((view) => !view.isMine);
  const inUse = all.find((view) => view.id === currentId) ?? null;
  const isChanged = inUse !== null && JSON.stringify(normalised(inUse.state)) !== JSON.stringify(normalised(current));

  function finish(): void {
    setIsNaming(false);
    setName('');
    setIsShared(false);
    close();
  }

  function create(): void {
    const trimmed = name.trim();
    if (trimmed === '') return;
    save.mutate(
      { name: trimmed, isShared, state: current },
      {
        onSuccess: (view) => {
          show({ tone: 'success', message: `View "${view.name}" saved`, detail: view.isShared ? 'Your colleagues can use it too.' : 'Only you can see it.' });
          onApply(view);
          finish();
        },
        onError: (error) => show({ tone: 'danger', message: "Couldn't save the view", detail: errorMessage(error) }),
      },
    );
  }

  return (
    <>
      <Button ref={triggerRef} variant="quiet" icon={Bookmark} onClick={toggle} aria-haspopup="menu" aria-expanded={isOpen}>
        {inUse ? inUse.name : 'Views'}
        {isChanged && <span className={styles.changed}> · changed</span>}
      </Button>
      <Popover anchor={anchor} onClose={finish} align="right" label="Saved views">
        {isNaming ? (
          <form
            className={styles.viewForm}
            onSubmit={(event) => {
              event.preventDefault();
              create();
            }}
          >
            <Field label="Name this view" isRequired>
              {({ inputId }) => <Input id={inputId} autoFocus value={name} maxLength={60} onChange={(event) => setName(event.target.value)} placeholder="Strong screeners" />}
            </Field>
            <Checkbox checked={isShared} onChange={setIsShared} label="Share with the team" />
            <div className={styles.filterFooter}>
              <Button variant="ghost" size="sm" onClick={() => setIsNaming(false)}>
                Back
              </Button>
              <Button type="submit" variant="solid" size="sm" disabled={name.trim() === ''} isPending={save.isPending}>
                Save view
              </Button>
            </div>
          </form>
        ) : (
          <>
            {mine.length > 0 && (
              <MenuGroup label="Your views">
                {mine.map((view) => (
                  <div key={view.id} className={styles.viewRow}>
                    <MenuItem
                      isSelected={view.id === currentId}
                      icon={view.isShared ? <Users size={14} aria-hidden /> : <Bookmark size={14} aria-hidden />}
                      meta={view.isShared ? 'Shared' : undefined}
                      onClick={() => {
                        onApply(view);
                        finish();
                      }}
                    >
                      {view.name}
                    </MenuItem>
                    <button
                      type="button"
                      className={styles.iconButton}
                      aria-label={`Delete the view ${view.name}`}
                      onClick={() => remove.mutate(view.id, { onSuccess: () => show({ tone: 'info', message: `View "${view.name}" deleted` }) })}
                    >
                      <Trash2 size={14} aria-hidden />
                    </button>
                  </div>
                ))}
              </MenuGroup>
            )}
            {shared.length > 0 && (
              <MenuGroup label="Shared by colleagues">
                {shared.map((view) => (
                  <MenuItem
                    key={view.id}
                    isSelected={view.id === currentId}
                    icon={<Users size={14} aria-hidden />}
                    meta={view.ownerName ?? undefined}
                    onClick={() => {
                      onApply(view);
                      finish();
                    }}
                  >
                    {view.name}
                  </MenuItem>
                ))}
              </MenuGroup>
            )}
            {all.length === 0 && <p className={styles.noViews}>No saved views yet. Set the filters and columns you want, then save them here.</p>}
            <MenuSeparator />
            {inUse?.isMine && isChanged && (
              <MenuItem
                icon={<Save size={14} aria-hidden />}
                onClick={() =>
                  update.mutate(
                    { id: inUse.id, body: { name: inUse.name, isShared: inUse.isShared, state: current, version: inUse.version } },
                    { onSuccess: () => show({ tone: 'success', message: `View "${inUse.name}" updated` }), onError: (error) => show({ tone: 'danger', message: "Couldn't update the view", detail: errorMessage(error) }) },
                  )
                }
              >
                Save changes to "{inUse.name}"
              </MenuItem>
            )}
            <MenuItem icon={<BookmarkPlus size={14} aria-hidden />} onClick={() => setIsNaming(true)}>
              Save this view…
            </MenuItem>
          </>
        )}
      </Popover>
    </>
  );
}

/** Compared by meaning, not by key order. */
function normalised(state: SavedViewState): SavedViewState {
  const filters = Object.fromEntries(Object.entries(state.filters).filter(([, value]) => value !== undefined && value !== '').sort(([a], [b]) => a.localeCompare(b)));
  return { filters, hiddenColumns: [...state.hiddenColumns].sort(), layout: state.layout };
}
